/**
 * BB Product Page Controller v4
 * ─────────────────────────────
 * Unified bundle confirmation + ATC intercept system.
 *
 * Key changes from v3:
 * - Detects bundle confirmation via cart state polling (Appstle-proof)
 * - Opens cart drawer using Dawn's native .open() after section refresh
 * - Single ATC intercept system (replaces dual click/submit approach)
 * - Adds .bb-bundle-mode class to product-info for CSS hooks
 *
 * Survives Shopify section re-renders via document-level MutationObserver.
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════
     Constants
     ══════════════════════════════════════ */

  // Selling plans per pack, read from the Liquid-rendered data-selling-plans
  // element (built from variant.selling_plan_allocations, so admin changes to
  // the plans flow through without a theme edit). Each entry is
  // { id, name, cadence, price }. Eric's 10 Sep spec: ONE plan per pack, and
  // none on the 180-Day (6 Pack) tier — an empty list means one-time only.
  var SELLING_PLANS = (function () {
    var el = document.querySelector('[data-selling-plans]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }());

  // Prices are read from the Liquid-rendered data-pack-prices element (region/currency-aware)
  var PACK_PRICES = (function () {
    var el = document.querySelector('[data-pack-prices]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }());

  // Pack the page lands on when the customer has not asked for a specific
  // variant. Walter, 2026-09-10. Must be one of the option VALUES.
  var DEFAULT_PACK = '3 Pack';

  // Appstle Mix & Match bundle reference IDs. 2 Pack is RETIRED (its card is
  // hidden) but stays listed so applyBundleDisplay can hide its embeds —
  // Appstle renders every bundle on the page regardless. 6 Pack subscribe
  // bundle created 2026-09-13 (Walter + Claude, Appstle admin).
  var BUNDLE_IDS = {
    '2 Pack': { onetime: 'cfki65v2i5', subscribe: '70glcwdm4o' },
    '3 Pack': { onetime: 'upaqnbgsmu', subscribe: 'yqrwlokc7z' },
    '6 Pack': { onetime: '2oj10qd8ij', subscribe: 'rm2xnqdalf' }
  };

  // Servings per single pack — one gummy a day, 30 per bag.
  var SERVINGS_PER_PACK = 30;


  /* ══════════════════════════════════════
     State
     ══════════════════════════════════════ */

  var state = {
    purchaseType: 'subscribe',
    preferredType: 'subscribe', // what the customer last chose; restored when a pack with plans is re-selected
    flavour: '',
    bundleConfirmed: false   // Set true when Appstle adds items to cart
  };

  var isUpdating = false;
  var boundElements = new WeakSet();

  /* ══════════════════════════════════════
     Helpers
     ══════════════════════════════════════ */

  var MONEY_FORMAT = (function () {
    var el = document.querySelector('[data-money-format]');
    if (!el) return '${{amount}}';
    try { return JSON.parse(el.textContent); } catch (e) { return '${{amount}}'; }
  }());

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var amountNoDec = Math.round(cents / 100).toString();
    return MONEY_FORMAT
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDec)
      .replace('{{amount_with_apostrophe_separator}}', amount.replace('.', "'"));
  }

  function getSelectedPack() {
    var el = document.querySelector('.bb-pack-selector input[type="radio"]:checked');
    return el ? el.value : '1 Pack';
  }

  /**
   * The single selling plan for a pack, or null when the pack is one-time only.
   */
  function getPlan(pack) {
    var plans = SELLING_PLANS[pack];
    return (plans && plans.length) ? plans[0] : null;
  }

  function packHasSubscription(pack) {
    return !!getPlan(pack);
  }

  /**
   * "Delivered every 90 days" from Appstle's raw option value
   * ("90DAY90DAY…", "2WEEK2WEEK…"). Falls back to the plan name.
   */
  function cadenceText(plan) {
    if (!plan) return '';
    var m = /^(\d+)\s*(DAY|WEEK|MONTH)/i.exec(plan.cadence || '');
    if (!m) return plan.name || '';
    var n = parseInt(m[1], 10);
    var unit = m[2].toLowerCase();
    return 'Delivered every ' + n + ' ' + unit + (n === 1 ? '' : 's');
  }

  function getQuantity() {
    var el = document.querySelector('quantity-input input[name="quantity"]');
    return el ? Math.max(1, parseInt(el.value, 10) || 1) : 1;
  }

  function getSelectedFlavour() {
    var el = document.querySelector('.bb-flavour-selector input[type="radio"]:checked');
    return el ? el.value : '';
  }

  /**
   * Returns true when Mix & Match is the active flavour on a 2+ Pack.
   */
  function isMixMatchActive() {
    var flavour = state.flavour || getSelectedFlavour();
    var pack = getSelectedPack();
    return flavour === 'Mix & Match' && !!BUNDLE_IDS[pack];
  }

  /**
   * Get the active Appstle bundle reference ID for current pack + purchase type.
   */
  function getActiveBundleId() {
    var pack = getSelectedPack();
    var config = BUNDLE_IDS[pack];
    if (!config) return null;
    return (state.purchaseType === 'subscribe' && config.subscribe) ? config.subscribe : config.onetime;
  }

  /**
   * Sort Appstle's frequency <option> elements into ascending numeric order.
   * Extracts the number from each option text (e.g. "Deliver every 12 weeks" → 12)
   * and sorts lowest to highest. Only runs once per <select>.
   */
  function sortFrequencyOptions(container) {
    var select = container.querySelector('select');
    if (!select || select._bbSorted) return;
    var opts = Array.from(select.options);
    opts.sort(function (a, b) {
      var numA = parseInt((a.text.match(/\d+/) || [0])[0], 10);
      var numB = parseInt((b.text.match(/\d+/) || [0])[0], 10);
      return numA - numB;
    });
    opts.forEach(function (opt) { select.appendChild(opt); });

    // Pre-select default frequency based on pack size
    var pack = getSelectedPack();
    var targetWeeks = (pack === '3 Pack') ? 12 : 8; // 3 Pack → 12 weeks, 2 Pack → 8 weeks
    for (var i = 0; i < select.options.length; i++) {
      var num = parseInt((select.options[i].text.match(/\d+/) || [0])[0], 10);
      if (num === targetWeeks) {
        select.selectedIndex = i;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }

    select._bbSorted = true;
  }

  /**
   * Returns true when the active Appstle bundle still needs items.
   */
  function isBundleIncomplete() {
    if (!isMixMatchActive()) return false;
    var bid = getActiveBundleId();
    if (!bid) return true;
    var bundleEl = document.querySelector('[appstle-dynamic-bundle-unique-reference="' + bid + '"]');
    if (!bundleEl) return true;
    var msg = bundleEl.querySelector('.ab-bundle-required-product-validation-message');
    if (!msg) return false;
    if (msg.offsetParent === null || msg.style.display === 'none') return false;
    var text = msg.textContent.trim();
    return text !== '' && text !== '0';
  }

  /* ══════════════════════════════════════
     Price Management
     ══════════════════════════════════════ */

  /**
   * Fill every pack card in Step 1 — price, struck compare, per-day and the
   * Save pill — from the same PACK_PRICES map the offer rows use, for the
   * purchase type and cadence currently selected.
   *
   * The Figma numbers do not reconcile (1 and 2 Packs are both labelled a
   * 60-Day Supply, and all three cards read 82c/day), so nothing here is
   * transcribed from the design: day supply is SERVINGS_PER_PACK x packs
   * and per-day is the real price divided by that. The cards cannot drift
   * from what the customer is actually charged.
   */
  function applyPackCards() {
    // Walter, 2026-09-13: frequency is now Step 1, ABOVE the packs, so each
    // card shows the figures for the purchase type the customer has already
    // chosen — AutoShip price when AutoShip is on and the pack has a plan,
    // one-time otherwise (the 180-Day tier has no plan, so it always shows
    // one-time). The pack total is still written but hidden by CSS; per-pack
    // and per-day are the headline.
    var wantSub = state.purchaseType === 'subscribe';
    document.querySelectorAll('.bb-pack-selector [data-pack-name]').forEach(function (wrap) {
      var name = wrap.getAttribute('data-pack-name');
      var prices = PACK_PRICES[name];
      if (!prices || !prices.onetime) return;

      var cardPlan = getPlan(name);
      var unit = (wantSub && cardPlan)
        ? ((cardPlan.price) || (prices.plans && prices.plans[cardPlan.id]) || prices.subscribe || prices.onetime)
        : prices.onetime;
      wrap.classList.toggle('bb-pack--onetime-only', wantSub && !cardPlan);
      var compare = (prices.compare && prices.compare > unit) ? prices.compare : 0;
      var packCount = parseInt(name, 10) || 1;
      var servings = SERVINGS_PER_PACK * packCount;

      var priceEl = wrap.querySelector('[data-pack-price]');
      if (priceEl) priceEl.textContent = formatMoney(unit);

      var compareEl = wrap.querySelector('[data-pack-compare]');
      if (compareEl) {
        compareEl.textContent = compare > 0 ? formatMoney(compare) : '';
        compareEl.hidden = compare <= 0;
      }

      var perpackEl = wrap.querySelector('[data-pack-perpack]');
      if (perpackEl) {
        perpackEl.innerHTML = '';
        perpackEl.appendChild(document.createTextNode(formatMoney(unit / packCount)));
        var small = document.createElement('small');
        small.textContent = '/pack';
        perpackEl.appendChild(small);
      }
      var perdayEl = wrap.querySelector('[data-pack-perday]');
      if (perdayEl) {
        perdayEl.textContent = formatMoney(unit / servings) + '/day';
      }

      var saveEl = wrap.querySelector('[data-pack-save]');
      if (saveEl) {
        var saved = compare > 0 ? compare - unit : 0;
        var pct = compare > 0 ? Math.round(saved / compare * 100) : 0;
        saveEl.textContent = saved > 0 ? 'Save ' + pct + '%' : ''; // % only (Walter, 2026-09-13)
        saveEl.hidden = saved <= 0;
      }
    });

    // Expose the selected pack as a class so CSS can react to it without a
    // sibling :has() chain — the step wrappers broke the old adjacency.
    var current = getSelectedPack();
    var host = document.querySelector('product-info') || document.querySelector('.product--information');
    if (host) {
      document.querySelectorAll('.bb-pack-selector [data-pack-name]').forEach(function (wrap) {
        var p = wrap.getAttribute('data-pack-name');
        host.classList.toggle('bb-pack-' + parseInt(p, 10), p === current);
      });
      host.classList.toggle('bb-no-subscribe', !packHasSubscription(current));
      // Ribbons: AutoShip shows ON SALE on the 90-Day only; one-time shows
      // Most Popular / Best Value (Walter, 2026-09-13).
      host.classList.toggle('bb-mode-onetime', !wantSub);
      host.classList.toggle('bb-mode-subscribe', wantSub);
    }

    // Step 1's heading echoes the choice — "1. Select your pack: 90-Day Gut Reset".
    var selectedEl = document.querySelector('[data-pack-selected]');
    if (selectedEl) {
      var wrapSel = document.querySelector('.bb-pack-selector [data-pack-name="' + current + '"]');
      var label = wrapSel ? wrapSel.getAttribute('data-tier-label') : '';
      selectedEl.textContent = label ? ': ' + label : '';
    }
  }

  function applyPrices() {
    if (isUpdating) return;
    isUpdating = true;

    var pack = getSelectedPack();
    var prices = PACK_PRICES[pack];
    var plan = getPlan(pack);
    var hasSub = !!plan;

    if (!prices) {
      isUpdating = false;
      return;
    }

    // A pack with no selling plan (180-Day) is one-time only: force the type
    // and remember what the customer actually chose so a pack that does
    // subscribe gets it back.
    var type = hasSub ? state.preferredType : 'onetime';
    state.purchaseType = type;

    var wrapper = document.querySelector('.bb-subscribe-wrapper');
    if (wrapper) wrapper.classList.toggle('bb-no-subscribe', !hasSub);

    var otp = document.querySelector('[data-onetime-price]');
    if (otp) otp.textContent = formatMoney(prices.onetime);

    // Subscribe price is the plan's own price (Appstle-discounted).
    var subUnit = hasSub
      ? ((plan.price) || (prices.plans && prices.plans[plan.id]) || prices.subscribe || prices.onetime)
      : prices.onetime;

    var subp = document.querySelector('[data-subscribe-price]');
    if (subp) subp.textContent = formatMoney(subUnit);

    // ── Struck prices ──
    // BOTH rows strike the compare-at (RRP) so they measure from the same
    // baseline — Walter, 2026-09-10: striking one-time on the subscribe row
    // made the one-time row look like the bigger saving. Falls back to the
    // one-time price only when there is no compare-at.
    var compare = (prices.compare && prices.compare > 0) ? prices.compare : 0;
    var subCompareCents = compare > subUnit
      ? compare
      : (prices.onetime > subUnit ? prices.onetime : 0);
    var subCompareEl = document.querySelector('[data-subscribe-compare]');
    if (subCompareEl) {
      subCompareEl.textContent = subCompareCents > 0 ? formatMoney(subCompareCents) : '';
      subCompareEl.style.display = subCompareCents > 0 ? '' : 'none';
    }

    var otCompareCents = compare > prices.onetime ? compare : 0;
    var otCompareEl = document.querySelector('[data-onetime-compare]');
    if (otCompareEl) {
      otCompareEl.textContent = otCompareCents > 0 ? formatMoney(otCompareCents) : '';
      otCompareEl.style.display = otCompareCents > 0 ? '' : 'none';
    }

    // ── Cadence line — from the plan, not a customer choice ──
    var cadence = cadenceText(plan);
    document.querySelectorAll('[data-subscribe-cadence]').forEach(function (el) {
      el.textContent = cadence;
      el.hidden = !cadence;
    });
    document.querySelectorAll('[data-subscribe-cadence-benefit]').forEach(function (el) {
      el.textContent = cadence || 'Delivered on schedule';
    });

    // ── Savings percentage vs the struck price ──
    var savingsPct = subCompareCents > 0 ? Math.round((1 - subUnit / subCompareCents) * 100) : 0;
    document.querySelectorAll('[data-savings-pct]').forEach(function (el) {
      el.textContent = savingsPct + '%';
    });
    document.querySelectorAll('.bb-subscribe-savings-pill').forEach(function (el) {
      el.hidden = savingsPct <= 0;
    });
    // Title pill (Walter, 2026-09-13): the AutoShip saving for the selected
    // pack. Under one-time the wording carries the condition so it stays true.
    document.querySelectorAll('[data-bb-title-save]').forEach(function (el) {
      if (savingsPct <= 0 || !hasSub) { el.hidden = true; el.textContent = ''; return; }
      el.hidden = false;
      el.textContent = 'Save ' + savingsPct + '%' + (type === 'subscribe' ? '' : ' with AutoShip');
    });

    // ── Price per serve (price ÷ servings in the pack) ──
    var packCount = parseInt(pack, 10) || 1;
    var packServings = SERVINGS_PER_PACK * packCount;
    var perdayEl = document.querySelector('[data-subscribe-perday]');
    if (perdayEl) {
      perdayEl.textContent = subUnit > 0 ? formatMoney(subUnit / packServings) + ' per serve' : '';
    }
    var otPerServeEl = document.querySelector('[data-onetime-perday]');
    if (otPerServeEl) {
      otPerServeEl.textContent = prices.onetime > 0 ? formatMoney(prices.onetime / packServings) + ' per serve' : '';
    }

    var spi = document.querySelector('[data-selling-plan-input]');
    if (spi) {
      spi.value = (type === 'subscribe' && plan) ? plan.id : '';
    }

    var qty = getQuantity();
    var unitPrice = type === 'subscribe' ? subUnit : prices.onetime;
    var pd = document.getElementById('price-display');
    if (pd) {
      pd.textContent = formatMoney(unitPrice * qty);
    }

    // ── Order summary under the ATC (Walter, 2026-09-13) ──
    // The total left the ATC button and the frequency cards; this is the
    // one place it is stated. Plain weight by design.
    var sumTotal = document.querySelector('[data-bb-summary-total]');
    if (sumTotal) sumTotal.textContent = formatMoney(unitPrice * qty);
    var sumCompare = document.querySelector('[data-bb-summary-compare]');
    if (sumCompare) {
      var sumCompareCents = type === 'subscribe' ? subCompareCents : otCompareCents;
      sumCompare.textContent = sumCompareCents > 0 ? formatMoney(sumCompareCents * qty) : '';
      sumCompare.hidden = sumCompareCents <= 0;
    }
    var sumLabel = document.querySelector('[data-bb-summary-label]');
    if (sumLabel) sumLabel.textContent = 'Total today';

    // A11y 2026-09-18 (M2, WCAG 4.1.3): every change above rewrites prices,
    // the pack heading and the summary silently. Say the outcome once the
    // arrow-key burst settles.
    announceSelection(pack, type, unitPrice * qty);

    // Lines under the total (Walter, 2026-09-13, revised later that day).
    // Shipping copy follows the General profile rule: Economy is free from a
    // $40 subtotal (Walter, 2026-09-23; was $50). The 3 and 6 Pack clear it
    // in both modes; the 1 Pack does not in either ($24.99 AutoShip / $29.99
    // one-time), so it gets the ship-time line only. The 2 Pack ($49.99)
    // would clear it but is retired from the PDP, so the >= 3 rule still
    // holds. No free shipping on 1 Pack subs (Walter's call).
    var packCountForLines = parseInt(pack, 10) || 1;
    var shipEl = document.querySelector('[data-bb-summary-shipping]');
    if (shipEl) {
      var freeShip = packCountForLines >= 3;
      shipEl.textContent = freeShip
        ? 'Free US shipping' + (type === 'subscribe' && plan ? ' on every order' : '') + '. Ships in 24–48 hours'
        : 'Ships in 24–48 hours';
    }
    var cancelEl = document.querySelector('[data-bb-summary-cancel]');
    if (cancelEl) {
      cancelEl.hidden = !(type === 'subscribe' && plan);
    }
    var guaranteeEl = document.querySelector('[data-bb-summary-guarantee]');
    if (guaranteeEl) {
      guaranteeEl.textContent = (packCountForLines * SERVINGS_PER_PACK) + '-day guarantee';
    }

    // Restore subscribe toggle state (Shopify re-render resets radios)
    var subRadio = document.getElementById(getSubscribeRadioId());
    var otRadio = document.getElementById(getOnetimeRadioId());
    if (type === 'subscribe' && subRadio && !subRadio.checked) {
      subRadio.checked = true;
    } else if (type === 'onetime' && otRadio && !otRadio.checked) {
      otRadio.checked = true;
    }

    applyPackCards();

    isUpdating = false;
    applyBundleDisplay();
  }

  /* ══════════════════════════════════════
     Flavour Management
     ══════════════════════════════════════ */

  function ensureFlavourSelected() {
    var pack = getSelectedPack();
    var flavour = getSelectedFlavour();

    // Two ways to end up without a usable flavour:
    //   - 1 Pack cannot Mix & Match, so M&M has to be replaced
    //   - the Mix & Match row was switched off, leaving nothing checked
    // Both submit an incomplete option set, so both get corrected here.
    var needsSingle = !flavour || (pack === '1 Pack' && flavour === 'Mix & Match');
    if (!needsSingle) return;

    // Prefer Berry Bliss, else the first flavour that is neither disabled
    // nor hidden. The M&M input lives outside .bb-flavour-btn-wrapper, so
    // it can never be picked as the fallback.
    var fallback =
      document.querySelector('.bb-flavour-selector input[type="radio"][value="Berry Bliss"]:not([disabled])')
      || document.querySelector('.bb-flavour-btn-wrapper:not(.bb-flavour-hidden) input[type="radio"]:not([disabled])');

    if (fallback && !fallback.checked) {
      fallback.checked = true;
      state.flavour = fallback.value;
      fallback.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ══════════════════════════════════════
     Bundle Display
     ══════════════════════════════════════ */

  function applyBundleDisplay() {
    var pack = getSelectedPack();
    var type = state.purchaseType;
    var flavour = state.flavour || getSelectedFlavour();

    var isMixMatch = flavour === 'Mix & Match';
    var bundleConfig = BUNDLE_IDS[pack];
    var activeBundleId = null;

    if (isMixMatch && bundleConfig) {
      activeBundleId = (type === 'subscribe' && bundleConfig.subscribe) ? bundleConfig.subscribe : bundleConfig.onetime;
    }

    // Collect all known bundle IDs
    var allBundleIds = [];
    Object.keys(BUNDLE_IDS).forEach(function (p) {
      if (BUNDLE_IDS[p].onetime) allBundleIds.push(BUNDLE_IDS[p].onetime);
      if (BUNDLE_IDS[p].subscribe) allBundleIds.push(BUNDLE_IDS[p].subscribe);
    });

    // Hide all, then show the active one
    allBundleIds.forEach(function (bid) {
      var selectors = [
        '[appstle-dynamic-bundle-unique-reference="' + bid + '"]',
        '.appstleDynamicBundle-' + bid
      ];
      selectors.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          if (bid === activeBundleId) {
            el.classList.add('bb-bundle-active');
            el.classList.remove('bb-bundle-hidden');
          } else {
            el.classList.remove('bb-bundle-active');
            el.classList.add('bb-bundle-hidden');
          }
        });
      });
    });

    // The parent product sits inside every bundle as the DISPLAY ANCHOR —
    // Appstle only renders a bundle on the product pages of products it
    // contains, and the bundle's selling plan must be common to every
    // product in it (2026-09-13, Walter + Claude, Appstle admin). Its card
    // is not a flavour, so hide it. Appstle's markup carries no product
    // attributes, so match on the card title text.
    hideParentCardsInBundles();
    removeParentTrayItems();
    syncBundleSellingPlan(activeBundleId);

    // Toggle bundle zone visibility
    var zone = document.querySelector('[data-bb-bundle-zone]');
    if (zone) {
      if (activeBundleId) {
        zone.removeAttribute('hidden');
        zone.classList.add('bb-bundle-zone--active');
      } else {
        zone.setAttribute('hidden', '');
        zone.classList.remove('bb-bundle-zone--active');
      }
    }

    // Toggle .bb-subscribe--bundle-mode on subscribe wrapper
    var subWrapper = document.querySelector('.bb-subscribe-wrapper');
    if (subWrapper) {
      if (isMixMatch && bundleConfig) {
        subWrapper.classList.add('bb-subscribe--bundle-mode');
      } else {
        subWrapper.classList.remove('bb-subscribe--bundle-mode');
      }
    }

    // Toggle .bb-bundle-mode on product-info (CSS uses this for qty/frequency hiding)
    var productInfo = document.querySelector('product-info') || document.querySelector('.product--information');
    if (productInfo) {
      if (isMixMatch && bundleConfig) {
        productInfo.classList.add('bb-bundle-mode');
      } else {
        productInfo.classList.remove('bb-bundle-mode');
      }
    }

    // Place compact subscribe toggle inside active bundle zone
    manageBundleSubscribeToggle(activeBundleId);

    // A11y 2026-09-18 (M8): name Appstle's per-flavour buttons.
    labelBundleButtons();
    setTimeout(labelBundleButtons, 600);
  }

  /**
   * Appstle's bundle checkout section carries its own selling-plan
   * <select> (#ab-bundle-selling-plan-dropdown) listing every plan common
   * to the bundle's products — both the 90-Day and the 180-Day live on the
   * same plan group, so both appear and Appstle defaults to the first. Pin
   * it to the plan whose name carries this tier's day count (packs × 30),
   * so a 3 Pack Mix & Match can never submit the 180-day plan. Re-run on
   * every display pass because the section is rebuilt as items are added.
   */
  function syncBundleSellingPlan(activeBundleId) {
    if (!activeBundleId) return;
    var bundleEl = document.querySelector('[appstle-dynamic-bundle-unique-reference="' + activeBundleId + '"]');
    if (!bundleEl) return;
    var sel = bundleEl.querySelector('#ab-bundle-selling-plan-dropdown, select[id*="selling-plan"]');
    if (!sel || !sel.options.length) return;
    var days = (parseInt(getSelectedPack(), 10) || 1) * SERVINGS_PER_PACK;
    var want = null;
    for (var i = 0; i < sel.options.length; i++) {
      if (new RegExp('^' + days + '[ -]*DAY', 'i').test(sel.options[i].textContent.trim())) { want = sel.options[i].value; break; }
    }
    if (want && sel.value !== want) {
      sel.value = want;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (want) sel.setAttribute('data-bb-pinned', want);
  }

  var PARENT_TITLE_RE = /^BoochBod Probiotic Kombucha Gummies/i;

  /**
   * Flavours the theme actually offers: the visible (not .bb-flavour-hidden),
   * non-disabled cards in the flavour picker — Berry Bliss + Lemon Lime today.
   * Ginger Beer is in every Appstle bundle (not launched, hidden in the picker)
   * so the bundle must be filtered to the same list (Walter, 2026-09-17).
   */
  function getOfferedFlavours() {
    var names = [];
    document.querySelectorAll('.bb-flavour-btn-wrapper:not(.bb-flavour-hidden)').forEach(function (w) {
      var input = w.querySelector('input[type="radio"]');
      var name = w.querySelector('.bb-flavour-name');
      if (input && input.disabled) return;
      var label = (input && input.value) || (name && name.textContent) || '';
      label = label.trim().toLowerCase();
      if (label) names.push(label);
    });
    return names;
  }

  /**
   * Hide every bundle card that is not an offered flavour: the parent product
   * (the display anchor, see applyBundleDisplay) and any unlaunched flavour
   * such as Ginger Beer. Appstle's markup carries no product attrs, so match
   * on the card title text. Cards are tagged .bb-bundle-parent-card (CSS hides).
   */
  function hideParentCardsInBundles() {
    var offered = getOfferedFlavours();
    document.querySelectorAll('[appstle-dynamic-bundle-unique-reference] .ab-bundle-product-info-card, [appstle-dynamic-bundle-unique-reference] [class*="ab-bundle-product-info"]').forEach(function (info) {
      var title = (info.textContent || '').trim();
      var titleLc = title.toLowerCase();
      var isParent = PARENT_TITLE_RE.test(title);
      var isOffered = offered.length && offered.some(function (f) { return titleLc.indexOf(f) === 0; });
      // With no picker on the page (or nothing offered) only the parent is hidden.
      if (!isParent && (isOffered || !offered.length)) return;
      // Walk up to the grid cell (the flex-col basis-[calc(...)] wrapper).
      var cell = info;
      for (var i = 0; i < 6 && cell && cell.parentElement; i++) {
        cell = cell.parentElement;
        if (/ab-basis-\[/.test(cell.className)) break;
      }
      if (cell) cell.classList.add('bb-bundle-parent-card');
    });
  }

  /**
   * Appstle rebuilds the "Your bundle" tray from any cart line that carries
   * its bundle id — including a PARENT-product line (the shopper, or a test on
   * LIVE where the parent card is still visible, added it). That line shows
   * as "1x BoochBod Probiotic Kombucha Gummies …" and counts toward the pack
   * (Walter, 2026-09-17). Click its ✕ so the tray only ever holds flavours.
   * Removing a tray item does not touch the cart. Re-runs on every
   * applyBundleDisplay() (the document observer fires on Appstle re-renders).
   */
  function removeParentTrayItems() {
    document.querySelectorAll('[appstle-dynamic-bundle-unique-reference] .ab-bundle-selected-product-remove-btn').forEach(function (btn) {
      var item = btn.parentElement;
      if (!item || item.hasAttribute('data-bb-removing')) return;
      var text = (item.textContent || '').replace(/^\s*✕\s*/, '').replace(/^\d+x\s*/, '').trim();
      if (!PARENT_TITLE_RE.test(text)) return;
      item.setAttribute('data-bb-removing', '1');
      btn.click();
    });
  }

  /* ══════════════════════════════════════
     Compact In-Bundle Subscribe Toggle
     ══════════════════════════════════════ */

  /**
   * Mix & Match: put the REAL Step 3 (heading, Subscribe & Save card, One-time
   * card, hidden selling_plan input) inside the active Appstle bundle zone,
   * directly above Appstle's add-to-cart button, and return it to its home
   * position when Mix & Match is switched off.
   *
   * Rewritten 2026-09-10 (Walter: "it should look identical"). The previous
   * version built a compact copy of the toggle from strings and moved
   * Appstle's frequency <select> into it; the copy had drifted from the real
   * card and the dropdown no longer fits a fixed-cadence plan. Moving the
   * real element means there is one markup, one stylesheet and one set of
   * radios. applyPrices() keeps writing into it wherever it lives.
   *
   * Appstle's own frequency <select> stays hidden (CSS). Wiring the bundle's
   * selling plan to the pack's single cadence is the functionality pass.
   */
  function manageBundleSubscribeToggle(activeBundleId) {
    var wrapper = document.querySelector('.bb-subscribe-wrapper');
    if (!wrapper) return;

    // Walter, 2026-09-13: frequency is Step 1 at the top of the buy box and
    // no longer travels into the bundle zone. Changing it still swaps the
    // one-time / subscribe bundle via applyBundleDisplay(). The price is off
    // Appstle's button too (data-bb-price left empty; CSS ::after reads it).
    wrapper.classList.remove('bb-subscribe--in-bundle');
    document.querySelectorAll('.ab-bundle-add-bundle-to-cart').forEach(function (btn) {
      btn.setAttribute('data-bb-price', '');
    });
    return;

    // Remember where Step 3 lives when not in a bundle (once).
    if (!wrapper._bbHome) {
      wrapper._bbHome = { parent: wrapper.parentNode, next: wrapper.nextElementSibling };
    }

    var moveTo = function (parent, before) {
      pauseObserver = true;
      parent.insertBefore(wrapper, before || null);
      setTimeout(function () { pauseObserver = false; }, 200);
    };

    if (!activeBundleId) {
      var home = wrapper._bbHome;
      var atHome = wrapper.parentNode === home.parent
        && (home.next ? wrapper.nextElementSibling === home.next : true);
      if (!atHome && home.parent) {
        var anchor = (home.next && home.next.parentNode === home.parent) ? home.next : null;
        moveTo(home.parent, anchor);
      }
      wrapper.classList.remove('bb-subscribe--in-bundle');
      return;
    }

    var bundleEl = document.querySelector(
      '[appstle-dynamic-bundle-unique-reference="' + activeBundleId + '"]'
    );
    if (!bundleEl) return;

    var confirmBtn = bundleEl.querySelector('.ab-bundle-add-bundle-to-cart');
    if (!confirmBtn) return;

    if (wrapper.nextElementSibling !== confirmBtn || wrapper.parentNode !== confirmBtn.parentNode) {
      moveTo(confirmBtn.parentNode, confirmBtn);
    }
    wrapper.classList.add('bb-subscribe--in-bundle');

    // Price on Appstle's button via data attribute (CSS ::after reads it).
    var pack = getSelectedPack();
    var prices = PACK_PRICES[pack];
    var plan = getPlan(pack);
    var subUnit = plan ? plan.price : (prices ? prices.onetime : 0);
    var btnPrice = prices
      ? formatMoney(state.purchaseType === 'subscribe' && plan ? subUnit : prices.onetime)
      : '';
    document.querySelectorAll('.ab-bundle-add-bundle-to-cart').forEach(function (btn) {
      btn.setAttribute('data-bb-price', btnPrice);
    });
  }

  /* ══════════════════════════════════════
     Cart Drawer — Refresh & Open
     ══════════════════════════════════════ */

  /**
   * Fetch fresh cart-drawer HTML from Shopify's section rendering API,
   * swap it into the existing <cart-drawer> element, then open the drawer
   * using Dawn's native .open() method.
   */
  function refreshAndOpenDrawer() {
    fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;

        var currentDrawer = document.querySelector('cart-drawer');
        if (!currentDrawer) return;

        // Parse fresh section HTML
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var freshDrawer = tmp.querySelector('cart-drawer');
        if (!freshDrawer) return;

        // Replace inner contents
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        currentDrawer.classList.remove('is-empty');

        // Open using Dawn's native method on next frame
        // (allows DOM to settle after innerHTML swap)
        requestAnimationFrame(function () {
          if (typeof currentDrawer.open === 'function') {
            currentDrawer.open();
          } else {
            // Fallback: manually trigger visible state
            currentDrawer.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
        });

        // Update cart count bubble
        updateCartCount();
      })
      .catch(function (err) {
        console.error('[BB] refreshAndOpenDrawer failed:', err);
      });
  }

  /**
   * Sync the header cart count bubble with actual cart state.
   */
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
          var span = bubble.querySelector('span[aria-hidden]');
          if (span) span.textContent = cart.item_count;
          if (cart.item_count > 0) bubble.classList.remove('hidden');
        });
      });
  }

  /* ══════════════════════════════════════
     Bundle Confirmation — Cart Polling
     ══════════════════════════════════════ */

  /**
   * Capture-phase listener on Appstle's "Confirm flavors" button.
   * When clicked, polls /cart.js to detect when Appstle finishes
   * adding items. Once cart count increases → mark confirmed + open drawer.
   *
   * This approach is Appstle-proof: doesn't rely on click events
   * bubbling (Appstle's React calls stopPropagation).
   */
  function initBundleConfirmWatcher() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.ab-bundle-add-bundle-to-cart');
      if (!btn || btn.disabled) return;
      if (!isMixMatchActive()) return;

      // Snapshot current cart count
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var prevCount = cart.item_count;
          var attempts = 0;
          var maxAttempts = 15; // 15 × 400ms = 6 seconds max

          var poller = setInterval(function () {
            attempts++;
            if (attempts > maxAttempts) {
              clearInterval(poller);
              console.warn('[BB] Bundle cart poll timed out');
              return;
            }

            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (newCart) {
                if (newCart.item_count > prevCount) {
                  clearInterval(poller);
                  state.bundleConfirmed = true;
                  refreshAndOpenDrawer();
                }
              });
          }, 400);
        });
    }, true); // capture phase — fires before Appstle's handler
  }

  /* ══════════════════════════════════════
     ATC Intercept — Unified
     ══════════════════════════════════════ */

  /**
   * Nudge user toward the bundle zone when they click ATC
   * without having confirmed their flavors.
   */
  function nudgeToBundleZone() {
    var bid = getActiveBundleId();
    if (!bid) return;

    var bundleEl = document.querySelector('[appstle-dynamic-bundle-unique-reference="' + bid + '"]');
    if (!bundleEl) return;

    // Scroll to bundle zone
    bundleEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Pulse the confirm button
    var confirmBtn = bundleEl.querySelector('.ab-bundle-add-bundle-to-cart');
    if (confirmBtn) {
      confirmBtn.classList.add('bb-pulse');
      setTimeout(function () { confirmBtn.classList.remove('bb-pulse'); }, 2000);
    }

    // Shake the validation message if visible
    var validation = bundleEl.querySelector('.ab-bundle-required-product-validation-message');
    if (validation && validation.offsetParent !== null) {
      validation.classList.add('bb-shake');
      setTimeout(function () { validation.classList.remove('bb-shake'); }, 600);
    }
  }

  /**
   * Single unified ATC intercept.
   *
   * Uses capture-phase submit listener so it fires before
   * product-form.js and survives Shopify section re-renders.
   *
   * In Mix & Match mode, the theme ATC NEVER submits the form
   * (parent variant should not be added). Instead:
   * - Not confirmed → nudge to bundle zone
   * - Confirmed → re-open cart drawer (items already in cart)
   */
  function initATCIntercept() {
    // Primary: intercept form submit
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
      if (!isMixMatchActive()) return;

      // Always block — parent variant must never be added for Mix & Match
      e.preventDefault();
      e.stopImmediatePropagation();

      if (state.bundleConfirmed) {
        refreshAndOpenDrawer();
      } else if (isBundleIncomplete()) {
        nudgeToBundleZone();
      } else {
        // Bundle is complete but user hasn't clicked Confirm yet
        nudgeToBundleZone();
      }
    }, true);

    // Backup: also intercept click on ATC button
    // (catches edge cases where click fires but submit doesn't)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-form__submit');
      if (!btn) return;
      if (!isMixMatchActive()) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      if (state.bundleConfirmed) {
        refreshAndOpenDrawer();
      } else {
        nudgeToBundleZone();
      }
    }, true);
  }

  /* ══════════════════════════════════════
     Radio ID Helpers
     ══════════════════════════════════════ */

  function getSubscribeRadioId() {
    var el = document.querySelector('.bb-subscribe-sub input[type="radio"]');
    return el ? el.id : '';
  }

  function getOnetimeRadioId() {
    var el = document.querySelector('.bb-subscribe-onetime input[type="radio"]');
    return el ? el.id : '';
  }

  /* ══════════════════════════════════════
     Event Binding
     ══════════════════════════════════════ */

  /**
   * Featured tier per purchase type (Walter, 16 Sep 2026). Returns true when a
   * different pack was selected (its change handler re-prices), false when the
   * featured tier was already checked or is unavailable.
   */
  var FEATURED_PACK = { subscribe: '3 Pack', onetime: '6 Pack' };

  function selectPackForType(type) {
    var want = FEATURED_PACK[type];
    if (!want) return false;
    var target = document.querySelector(
      '.bb-pack-selector input[type="radio"][value="' + want + '"]'
    );
    if (!target || target.disabled || target.checked) return false;
    if (target.closest('.bb-pack-hidden')) return false;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /* ── A11y helpers (2026-09-18) ─────────────────────────────────────── */

  var announceTimer = null;
  var announcePrimed = false;
  var announceLast = '';

  /* The live region lives on <body>, NOT in the buy box: Dawn re-renders the
     whole product-info section on every variant change, and an element
     inside it is destroyed before the debounce fires (proved on the draft,
     2026-09-18 — the old node was disconnected and its text never spoke). */
  function getLiveRegion() {
    var live = document.getElementById('bb-buybox-live');
    if (live && live.isConnected) return live;
    live = document.createElement('div');
    live.id = 'bb-buybox-live';
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    document.body.appendChild(live);
    return live;
  }

  function announceSelection(pack, type, totalCents) {
    var wrapper = document.querySelector('.bb-pack-btn-wrapper[data-pack-name="' + pack + '"]');
    var tier = (wrapper && wrapper.getAttribute('data-tier-label')) || pack;
    var flavour = getSelectedFlavour();
    var text = tier + ', ' + (type === 'subscribe' ? 'AutoShip' : 'one-time purchase')
      + (flavour ? ', ' + flavour : '') + ', ' + formatMoney(totalCents);
    if (text === announceLast) return;
    announceLast = text;
    // The first pass is the page's own default selection — the reader is
    // already reading the buy box, so prime the comparison and stay quiet.
    if (!announcePrimed) { announcePrimed = true; return; }
    clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      var live = getLiveRegion();
      live.textContent = '';
      // A clear then a re-write, so identical text still re-announces.
      // setTimeout, not requestAnimationFrame: rAF never fires in a
      // backgrounded tab, and an announcement must not depend on paint.
      setTimeout(function () { live.textContent = text; }, 50);
    }, 400);
  }

  /* M8: Appstle renders one "Add to bundle" per flavour card with no
     flavour in the name (WCAG 2.4.6 / 4.1.2). Name it from the card. */
  function labelBundleButtons() {
    var active = document.querySelector('[appstle-dynamic-bundle-unique-reference].bb-bundle-active');
    if (!active) return;
    active.querySelectorAll('button').forEach(function (btn) {
      var txt = (btn.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/^add to bundle$/i.test(txt) || btn.hasAttribute('aria-label')) return;
      var node = btn, name = '';
      for (var i = 0; i < 6 && node && node !== active && !name; i++) {
        node = node.parentElement;
        if (!node) break;
        var img = node.querySelector('img[alt]');
        if (img && img.alt.trim()) { name = img.alt.trim(); break; }
        var t = node.querySelector('[class*="title"], [class*="name"], h1, h2, h3, h4, h5, h6');
        if (t && t.textContent.trim()) { name = t.textContent.replace(/\s+/g, ' ').trim(); break; }
      }
      if (name) btn.setAttribute('aria-label', 'Add ' + name + ' to bundle');
    });
  }

  /* Mix & Match row. It is a radio because "Mix & Match" is a real option
     value Appstle keys its bundles on, but the design presents it as a
     checkbox — so a click while it is already active has to turn it off.
     A radio cannot do that itself, hence the pointerdown intercept: by the
     time `change` fires the radio is already checked.

     2026-09-18: these were bound per-element inside bindEvents(), and were
     found on the draft to be missing entirely after a variant re-render —
     the row survived but carried no handlers, so Mix & Match could not be
     switched off by mouse OR keyboard. Delegating from `document` once
     survives every re-render, the same approach bb-cart.js already takes
     for the drawer. The keyboard branch is the accessibility fix (M7,
     WCAG 2.1.1): pointerdown never fires for a Space press.

     pointerdown is watched on the ROW, not the input: the input is the
     visually-hidden half of a label pair, so a press lands on the label. */
  var mixMatchDelegated = false;

  function uncheckMixMatch(input) {
    input.checked = false;
    state.flavour = '';
    state.bundleConfirmed = false;
    ensureFlavourSelected();
    applyBundleDisplay();
  }

  function mixMatchInputFrom(target) {
    if (!target || !target.closest) return null;
    var row = target.closest('[data-mix-match-row]');
    if (row) return row.querySelector('[data-mix-match-input]');
    return target.closest('[data-mix-match-input]');
  }

  function bindMixMatchToggle() {
    if (mixMatchDelegated) return;
    mixMatchDelegated = true;

    document.addEventListener('pointerdown', function (e) {
      var input = mixMatchInputFrom(e.target);
      if (!input) return;
      if (input.checked) input.dataset.bbUncheck = '1';
      else delete input.dataset.bbUncheck;
    }, true);

    document.addEventListener('click', function (e) {
      var input = mixMatchInputFrom(e.target);
      if (!input) return;
      if (input.dataset.bbUncheck !== '1') return;
      delete input.dataset.bbUncheck;
      uncheckMixMatch(input);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== ' ' && e.key !== 'Spacebar') return;
      var input = e.target && e.target.closest && e.target.closest('[data-mix-match-input]');
      if (!input || !input.checked) return;
      e.preventDefault();
      uncheckMixMatch(input);
    });
  }

  function bindEvents() {
    bindMixMatchToggle();

    // Pack radios
    document.querySelectorAll('.bb-pack-selector input[type="radio"]').forEach(function (radio) {
      if (boundElements.has(radio)) return;
      boundElements.add(radio);
      radio.addEventListener('change', function () {
        state.bundleConfirmed = false; // Reset on pack change
        ensureFlavourSelected();
        applyPrices();
      });
    });

    // Flavour radios
    document.querySelectorAll('.bb-flavour-selector input[type="radio"]').forEach(function (radio) {
      if (boundElements.has(radio)) return;
      boundElements.add(radio);
      radio.addEventListener('change', function () {
        state.flavour = radio.value;
        state.bundleConfirmed = false; // Reset on flavour change
        applyBundleDisplay();
      });
    });

    // Purchase type radios
    document.querySelectorAll('input[name="bb-purchase-type"]').forEach(function (radio) {
      if (boundElements.has(radio)) return;
      boundElements.add(radio);
      radio.addEventListener('change', function () {
        state.purchaseType = radio.value;
        state.preferredType = radio.value;
        state.bundleConfirmed = false; // Reset on OTP/Sub switch
        // Walter, 16 Sep 2026: each purchase type lands on its featured tier —
        // AutoShip → 90-Day (3 Pack), One-time → 180-Day (6 Pack). Ticking the
        // pack radio and dispatching change runs the pack handler above (flavour
        // guard + applyPrices) and lets Dawn swap the variant, exactly as a tap
        // on the card would. If that tier is already selected, just re-price.
        if (!selectPackForType(radio.value)) applyPrices();
      });
    });

    // Frequency dropdown and pills removed 2026-09-10: cadence is fixed per pack.

    // Mix & Match is delegated from `document` — see bindMixMatchToggle().

    // Quantity input
    document.querySelectorAll('quantity-input input[name="quantity"]').forEach(function (input) {
      if (boundElements.has(input)) return;
      boundElements.add(input);
      input.addEventListener('change', function () { applyPrices(); });
    });
    document.querySelectorAll('quantity-input button').forEach(function (btn) {
      if (boundElements.has(btn)) return;
      boundElements.add(btn);
      btn.addEventListener('click', function () {
        setTimeout(applyPrices, 50);
      });
    });
  }

  /* ══════════════════════════════════════
     Setup + Observer + Init
     ══════════════════════════════════════ */

  /**
   * Land the page on the default pack (3 Pack / 90-Day), with subscribe already
   * selected via `state.purchaseType`.
   *
   * Only when the customer has NOT asked for a specific variant. A
   * `?variant=` in the URL comes from an ad, an email or a shared link and
   * must be honoured — overriding it would send paid traffic to the wrong
   * pack.
   *
   * Dispatching `change` is what Dawn's variant picker listens for, so this
   * drives a real variant swap rather than just ticking a radio. Dawn then
   * writes `?variant=` into the URL, which is what stops this re-firing on
   * the re-render it causes.
   */
  // The landing default is applied ONCE per page. setup() re-runs on every
  // DOM mutation (Dawn re-renders product-info after each variant swap), and
  // the reload rule below is true for the whole life of a reloaded page — so
  // without this flag every click on another tier was snapped straight back
  // to 3 Pack + AutoShip (Walter, 17 Sep 2026: "the website fights me").
  var defaultPackDone = false;

  function ensureDefaultPack() {
    // A retired pack (2 Pack — card rendered hidden) can still arrive
    // checked via an old ?variant= link. That one is overridden even though
    // the customer "asked" for it: there is no card to show them.
    var checkedNow = document.querySelector('.bb-pack-selector input[type="radio"]:checked');
    var checkedHidden = !!(checkedNow && checkedNow.closest('.bb-pack-hidden'));

    if (defaultPackDone && !checkedHidden) return;

    // A hard refresh is a fresh landing, not an inbound link (Walter, 16 Sep
    // 2026): Dawn wrote the last-picked variant into the URL, so without this
    // a reload re-landed on that tier (e.g. 180-Day under the AutoShip pill).
    // Only `reload` is treated this way — back/forward and real links keep
    // their variant.
    var isReload = false;
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      isReload = !!(nav && nav.type === 'reload');
    } catch (e) { /* ignore */ }

    try {
      if (!checkedHidden && !isReload && new URLSearchParams(window.location.search).has('variant')) return;
    } catch (e) {
      return; // no URLSearchParams — leave the merchant default alone
    }

    var target = document.querySelector(
      '.bb-pack-selector input[type="radio"][value="' + DEFAULT_PACK + '"]'
    );
    if (!target || target.disabled) return;
    defaultPackDone = true;

    var changed = false;

    // The default pack must not drag Mix & Match in with it. Landing on a
    // multi-pack without pinning the flavour can resolve to the Mix & Match
    // variant and tick that row for a customer who never asked for it.
    //
    // Dawn resolves the variant from every checked option input at once, so
    // both are set here and a single change is dispatched afterwards.
    var flavour = document.querySelector('.bb-flavour-selector input[type="radio"]:checked');
    if (!flavour || flavour.value === 'Mix & Match') {
      var single =
        document.querySelector('.bb-flavour-selector input[type="radio"][value="Berry Bliss"]:not([disabled])')
        || document.querySelector('.bb-flavour-btn-wrapper:not(.bb-flavour-hidden) input[type="radio"]:not([disabled])');
      if (single && !single.checked) {
        single.checked = true;
        state.flavour = single.value;
        changed = true;
      }
    }

    if (!target.checked) {
      target.checked = true;
      changed = true;
    }

    // Land on AutoShip too: a reload keeps the browser's restored radio state,
    // so the one-time pill could still be ticked from before the refresh.
    var subRadio = document.querySelector('input[name="bb-purchase-type"][value="subscribe"]');
    if (subRadio && !subRadio.checked) {
      subRadio.checked = true;
      changed = true;
    }
    state.purchaseType = 'subscribe';
    state.preferredType = 'subscribe';

    // Dispatching `change` is what Dawn's variant picker listens for, so this
    // drives a real variant swap rather than just ticking radios. Dawn then
    // writes `?variant=` into the URL, which is what stops this re-firing on
    // the re-render it causes.
    if (changed) {
      // Phones (Walter, 17 Sep 2026): Dawn's media-gallery.setActiveMedia()
      // window.scrollTo()s to the swapped-in variant image after this change,
      // which reads as "the page jumped to the buy box" on load. Flag the
      // landing swap so media-gallery.js skips that scroll (only that one).
      document.documentElement.setAttribute('data-bb-landing', '1');
      setTimeout(function () { document.documentElement.removeAttribute('data-bb-landing'); }, 2500);
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function setup() {
    ensureDefaultPack();
    state.flavour = getSelectedFlavour();
    bindEvents();
    ensureFlavourSelected();
    applyPrices(); // also calls applyBundleDisplay
  }

  var setupTimer = null;
  var pauseObserver = false;
  var observer = new MutationObserver(function () {
    if (pauseObserver) return;
    clearTimeout(setupTimer);
    setupTimer = setTimeout(function () {
      if (document.querySelector('.bb-subscribe-wrapper')) {
        setup();
      }
    }, 100);
  });

  function startObserver() {
    var target = document.querySelector('.product__info-wrapper')
      || document.querySelector('main')
      || document.body;
    observer.observe(target, { childList: true, subtree: true });
  }

  function init() {
    if (!document.querySelector('.bb-subscribe-wrapper')) return;
    setup();
    startObserver();
  }

  /* ──────────────────────────────────────────
     Suppress the initial programmatic focus ring
     ──────────────────────────────────────────
     On load, ensureFlavourSelected() may auto-correct the flavour, which
     triggers a product-info re-render that focuses the flavour radio. The
     browser paints :focus-visible for that programmatic focus, leaving a
     stray outline around the preselected flavour. We mark the document as
     "pre-interaction" so CSS can hide that ring, and clear the mark the
     instant the user does anything — so genuine keyboard focus rings return. */
  document.documentElement.classList.add('bb-init');
  ['pointerdown', 'keydown', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      document.documentElement.classList.remove('bb-init');
    }, { once: true, capture: true });
  });

  /* ── Register document-level intercepts (once, survive re-renders) ── */
  initATCIntercept();
  initBundleConfirmWatcher();

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
